import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { DateRange as DatePicker } from 'react-date-range'
import ReactMarkdown from 'react-markdown'
import classNames from 'classnames'
// import numeral from 'numeraljs'
// import shortid from 'shortid'

import { SiteState } from '../store/site-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import { ClickEditInput } from '../components/input'
import SiteWrapper from '../components/sitewrapper'
import Layout from '../components/layout'
import Intro from '../components/intro'
import Button, { ButtonRow } from '../components/button'
import styles from '../component-styles/performance-report.module.scss'
import ToolTip from '../components/tooltip'
import { saveUserData, getUserData } from '../local-client/local-client'
// import { MultiSelectBox } from '../components/select-box'
import SelectBoxTypeahead from '../components/select-box-typeahead'
import useResize from '../hooks/useResize'
import {
  matchTypesDropDown,
  siteContainerWidth,
  performanceDates,
  granularityFilterData,
  dateFormatShort,
  messages,
} from '../core/variables'

import {
  getItemByKeyValue,
  getDefaultOption,
  sortData,
  getKeyFromMetricValue,
  hashCode,
} from '../helpers'
import AnomalyDiff from '../components/anomaly-diff'
import uplifter from '../assets/uplogo.png'
import CollapsableFilters, {
  SupportMetricDropDown,
  CollapsableFiltersSummary,
} from '../components/collapsable-filters'
import MetricDataBanner from '../components/metric-data-banner'
import LinksTable from '../components/links-table'
import {
  getCampaignLinkDashboardMetricDataGQL,
  getCampaignLinkDashboardMetricClickDataGQL,
  getCampaignLinkDashboardGraphGQL,
  getCampaignLinkDashboardGraphClickDataGQL,
  getCampaignLinkDashboardTableGQL,
  getCampaignLinkDashboardMetaDefaultsGQL,
  getUserAccountDetailsGQL,
  logActionAsync,
  updateSavedLinkPerformanceReportGQL,
} from '../store/actions'
import ConnectAnalyticsBlocker from '../components/connect-analytics-blocker'
import PerformanceReportGraph from '../components/performance-report-graph'
import Manage from '../components/explain-manage'
import { LoadingLabel, Preloader } from '../components/loader'
import { ImgUploader } from '../components/img-uploader'
import ManageReportsTabs, {
  ManageReportsTabsSaveButton,
} from '../components/manage-reports-tabs'
import { uploadAccountLogo } from '../api-client/graphql-client'
import AcquisitionActions from '../components/acquisition-actions'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface LogoProps {
  logoLink: string
  loading: boolean
}
export function Logo({ logoLink, loading }: LogoProps): React.ReactElement {
  if (loading) {
    return (
      <div
        className={classNames(styles.logoWrapper, {
          [styles.logoLoading]: loading,
        })}
      >
        <Preloader />
      </div>
    )
  }
  return (
    <div className={styles.logoWrapper}>
      <img src={logoLink} alt="Account logo" />
    </div>
  )
}

interface EditablePageTitleProps {
  setBreakdownDimension: (val: string) => void
  dateRangeLabel: string
  breakdownDimension: string
  availableDimensions: any[]
  successMetricDisplayName: string
  loading: boolean
}

export function EditablePageTitle({
  setBreakdownDimension,
  dateRangeLabel,
  breakdownDimension,
  availableDimensions,
  successMetricDisplayName,
  loading,
}: EditablePageTitleProps): React.ReactElement {
  const [titleActive, setTitleActive] = useState(false)
  const dispatch = useDispatch()

  const useAvailableDimensions = useMemo(() => {
    return availableDimensions
      ? [
          {
            optionName: 'No breakdown',
            optionValue: '',
          },
          ...sortData(
            availableDimensions.map((item) => {
              const { dimensionName } = item
              return {
                optionName: dimensionName,
                optionValue: dimensionName,
              }
            }),
            'optionName',
            true,
            null,
            true,
          ),
        ]
      : []
  }, [availableDimensions])

  if (loading || successMetricDisplayName === '') {
    return <></>
  }

  return (
    <h1
      className={styles.graphTitle}
      onMouseEnter={() => {
        setTitleActive(true)
      }}
      onMouseLeave={() => {
        setTitleActive(false)
      }}
    >
      <span>
        {successMetricDisplayName} {dateRangeLabel}
        {breakdownDimension === '' ? '' : ` split by `}
      </span>
      <span className={styles.titleSelectWrapper}>
        <SelectBoxTypeahead
          ignoreOrder
          elementActive={titleActive}
          className={styles.successMetricSelector}
          id="breakdownDimension"
          showDefault
          showValueWhenNotActive
          placeholder="Please select"
          keyName="optionName"
          keyValue="optionValue"
          value={breakdownDimension}
          onInputChange={(val: string) => {
            setBreakdownDimension(val)
            const actionData = {
              action: 'graph-breakdown-updated',
              longDescription: val,
              websiteSection: 'report',
            }
            dispatch(logActionAsync(actionData))
          }}
          items={useAvailableDimensions}
        />
      </span>
    </h1>
  )
}

// export const defaultReportOptions = {
//   applyFilters: [],
//   endDate: yesterday,
//   startDate: moment(yesterday).subtract(30, 'days').toDate(),
//   matchType: 'full',
//   selectedMetric: 'ga:sessions',
//   stackDimension: '',
// }

export default function PerformanceReportPage(): React.ReactElement {
  const [hash, setHash] = useState(0)
  const [filtersHaveChanged, setFiltersHaveChanged] = useState(false)
  const [currentReport, setCurrentReport] = useState<any>(null)
  const [loadGraphData, setLoadGraphData] = useState(false)
  const [showTopLinksTable, setShowTopLinksTable] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [matchType, setMatchType] = useState(matchTypesDropDown[0])
  const [defaultSet, setDefaultSet] = useState(false)
  const [granularity, setGranularity] = useState('monthly')
  const [errorLoadingInterface, setErrorLoadingInterface] = useState(false)
  const [successMetric, setSuccessMetric] = useState('')
  const [successMetricsList, setSuccessMetricsList] = useState<any[]>([])
  const [breakdownDimension, setBreakdownDimension] = useState('')
  const [selectedDimensions, setSelectedDimensions] = useState<any>({})
  const [dashboardMetricData, setDashboardMetricData] = useState<any>({})
  const [dashboardMetricClickData, setDashboardMetricClickData] = useState<any>(
    {},
  )
  const [graphData, setGraphData] = useState<any | null>(null)
  const [graphDateRange, setGraphDateRange] = useState<any | null>(null)
  const [graphType, setGraphType] = useState('bar')

  const [graphClickData, setGraphClickData] = useState<any | null>(null)
  const [graphDateRangeClickData, setGraphDateRangeClickData] = useState<any>(
    null,
  )
  const [graphTypeClickData, setGraphTypeClickData] = useState('bar')

  const [width, setWidth] = useState(0)
  const [currentDate, setCurrentDate] = useState('thisYear')
  const [startDate, setStartDate] = useState(
    moment(yesterday).subtract(365, 'days').toDate(),
  )
  const [endDate, setEndDate] = useState(yesterday)
  const [calendarVisible, setCalendarVisible] = useState(true)
  const [titleValue, setTitleValue] = useState(messages.defaultReportTitle)

  const [showHighlight, setShowHighlight] = useState(true)

  const dispatch = useDispatch()

  const {
    site: { selected },
    graphql,
  }: { site: SiteState; graphql: GraphQLState } = useSelector(
    (s: any): any => s,
  )
  const {
    campaignLinkDashboardMetricData,
    campaignLinkDashboardMetricDataClickData,
    campaignLinkDashboardGraph,
    campaignLinkDashboardGraphClickData,
    campaignLinkDashboardTableRows,
    campaignLinkDashboardMeta,
    error: graphqlErrors,
    loading: graphqlLoading,
    currentAccount,
    currentUser,
  } = graphql

  const currentAccountPermission = currentUser?.currentAccountPermission || ''

  const isSupportUser = currentAccountPermission === 'support'

  const isAdminUser = currentAccountPermission === 'admin'

  const useLogo = currentAccount?.logoLink || uplifter
  const logoLink =
    useLogo.indexOf('logos/PNG/uplogo.png') !== -1 ? uplifter : useLogo

  useEffect(() => {
    if (!currentAccount) {
      dispatch(getUserAccountDetailsGQL())
    }
    if (currentAccount && currentAccount.accountID) {
      const savedData = getUserData(currentAccount.accountID)

      if (
        savedData &&
        Object.prototype.hasOwnProperty.call(savedData, 'showHighlight')
      ) {
        setShowHighlight(savedData.showHighlight)
      } else {
        setShowHighlight(true)
      }
    }
  }, [currentAccount])

  const updateShowHighlight = () => {
    saveUserData(
      {
        showHighlight: !showHighlight,
      },
      currentAccount?.accountID,
    )
    setShowHighlight(!showHighlight)
  }

  useEffect(() => {
    if (campaignLinkDashboardMetricData) {
      setDashboardMetricData(campaignLinkDashboardMetricData)
    }
  }, [campaignLinkDashboardMetricData])

  useEffect(() => {
    if (campaignLinkDashboardMetricDataClickData) {
      setDashboardMetricClickData(campaignLinkDashboardMetricDataClickData)
    }
  }, [campaignLinkDashboardMetricDataClickData])

  useEffect(() => {
    if (successMetric) {
      const useSuccessMetricKey = getKeyFromMetricValue(successMetric)
      if (
        campaignLinkDashboardGraph &&
        campaignLinkDashboardGraph[useSuccessMetricKey]
      ) {
        setGraphDateRange(
          campaignLinkDashboardGraph[useSuccessMetricKey].dateRange,
        )
        setGraphData(campaignLinkDashboardGraph[useSuccessMetricKey].graphData)
        setGraphType(campaignLinkDashboardGraph[useSuccessMetricKey].graphType)
      }
    }
  }, [campaignLinkDashboardGraph])

  useEffect(() => {
    if (campaignLinkDashboardGraphClickData) {
      setGraphDateRangeClickData(campaignLinkDashboardGraphClickData.dateRange)
      setGraphClickData(campaignLinkDashboardGraphClickData.graphData)
      setGraphTypeClickData(campaignLinkDashboardGraphClickData.graphType)
    }
  }, [campaignLinkDashboardGraphClickData])

  useEffect(() => {
    if (campaignLinkDashboardMeta && currentAccount) {
      if (!defaultSet) {
        const mT = getDefaultOption(
          campaignLinkDashboardMeta,
          'onloadDefaultOptions',
          'matchType',
          'full',
        )

        if (matchType.value !== mT) {
          setMatchType({
            value: mT,
            name: mT,
            shortName: mT,
            tooltip: '',
          })
        }
        setDefaultSet(true)
      }
      const {
        availableMetrics,
        availableDimensions,
        availableClickMetrics,
      } = campaignLinkDashboardMeta
      if (
        breakdownDimension === '' &&
        availableDimensions &&
        availableDimensions.length > 0
      ) {
        const useData = [
          ...sortData(availableDimensions, 'dimensionName', true, null, true),
        ]
        setBreakdownDimension(useData[0].dimensionName)
        setLoadGraphData(true)
      }
      if (
        (successMetric === '' || typeof successMetric === 'undefined') &&
        availableMetrics &&
        availableMetrics.length > 0
      ) {
        setSuccessMetric(availableMetrics[0].metricID)

        const useClickMetrics = availableClickMetrics || []
        let useList = [...availableMetrics, ...useClickMetrics]
          .filter((i) => i !== '')
          .map((i) => i.metricID)

        if (currentAccount.accountID) {
          const savedData = getUserData(currentAccount.accountID)
          if (
            savedData &&
            Object.prototype.hasOwnProperty.call(
              savedData,
              'successMetricsList',
            )
          ) {
            useList = savedData.successMetricsList
          }
        }
        setSuccessMetricsList(useList)
      }
    }
  }, [campaignLinkDashboardMeta, currentAccount])

  useEffect(() => {
    if (graphqlErrors.length > 0) {
      if (graphqlErrors.indexOf('campaignLinkDashboardMeta') !== -1) {
        setErrorLoadingInterface(true)
      } else {
        setErrorLoadingInterface(false)
      }
    }
  }, [graphqlErrors])

  const screenWidth = useResize()
  const graphWidth: number = useMemo(() => {
    let res: number
    if (width) {
      res = width
    } else if (screenWidth > 1260) {
      res = 1108
    } else if (screenWidth < siteContainerWidth) {
      res = screenWidth - 40
    } else if (screenWidth <= siteContainerWidth + 150) {
      res = screenWidth - 150
    } else {
      res = 960
    }
    return res - 34 - 40
  }, [width, screenWidth])

  const isMobile = screenWidth <= 768
  let offset = granularity === 'monthly' || granularity === 'quarterly' ? 0 : 40
  if (isMobile && offset > 0) {
    offset = 20
  }

  const {
    availableDimensions,
    dataSourceDescription,
    availableMetrics,
    availableClickMetrics,
  } = campaignLinkDashboardMeta || {
    availableDimensions: [],
    dataSourceDescription: '',
    availableMetrics: [''],
    availableClickMetrics: [''],
  }
  // reportTitle || messages.defaultReportTitle

  const matchTypeMerged = useMemo(() => {
    if (!availableDimensions) {
      return [...matchTypesDropDown]
    }
    return [
      ...matchTypesDropDown,
      ...sortData(
        availableDimensions.map((item) => {
          const { dimensionName, helpText } = item
          return {
            name: dimensionName,
            shortName: dimensionName,
            value: dimensionName,
            tooltip: helpText,
          }
        }),
        'name',
        true,
        null,
        true,
      ),
    ]
  }, [availableDimensions])

  const allAvailableMetrics = useMemo(() => {
    const useClickMetrics = availableClickMetrics || []
    if (!availableMetrics || !useClickMetrics) {
      return []
    }
    const res = [...availableMetrics, ...useClickMetrics].filter(
      (i) => i !== '' && i.setupComplete,
    )
    return res
  }, [availableMetrics, availableClickMetrics])

  const getLinkDashboardMetricData = () => {
    const result = getItemByKeyValue(
      allAvailableMetrics,
      'metricID',
      successMetric,
    )
    if (result !== -1 && result.isUplifterClickMetric) {
      dispatch(
        getCampaignLinkDashboardMetricClickDataGQL({
          currentDate,
          startDate,
          endDate,
          selectedDimensions,
          successMetric,
          granularity,
        }),
      )
    } else {
      dispatch(
        getCampaignLinkDashboardMetricDataGQL({
          currentDate,
          startDate,
          endDate,
          selectedDimensions,
          successMetric,
          granularity,
          matchType: matchType.value,
        }),
      )
    }
  }

  const getLinkDashboardMetricGraphData = () => {
    const result = getItemByKeyValue(
      allAvailableMetrics,
      'metricID',
      successMetric,
    )

    if (result !== -1 && result.isUplifterClickMetric) {
      dispatch(
        getCampaignLinkDashboardGraphClickDataGQL({
          selectedDimensions,
          successMetric,
          breakdownDimension,
          currentDate,
          startDate,
          endDate,
          granularity,
        }),
      )
    } else {
      dispatch(
        getCampaignLinkDashboardGraphGQL({
          selectedDimensions,
          successMetric,
          breakdownDimension,
          currentDate,
          startDate,
          endDate,
          granularity,
          matchType: matchType.value,
        }),
      )
    }
  }

  const getTableData = () => {
    const payload = {
      matchType: matchType.value,
      selectedDimensions,
      successMetric,
      currentDate,
      startDate,
      endDate,
    }
    dispatch(getCampaignLinkDashboardTableGQL(payload))
  }

  useEffect(() => {
    dispatch(getCampaignLinkDashboardMetaDefaultsGQL())
    // dispatch(getCampaignLinkDashboardMetaGQL())
  }, [])

  useEffect(() => {
    if (loadGraphData && successMetric !== '' && successMetric !== '') {
      getLinkDashboardMetricData()
      getLinkDashboardMetricGraphData()
    }
  }, [
    selectedDimensions,
    successMetric,
    breakdownDimension,
    granularity,
    currentDate,
    startDate,
    endDate,
    matchType,
    loadGraphData,
  ])

  useEffect(() => {
    if (defaultSet && currentReport !== null) {
      const currentHash = hashCode(
        JSON.stringify({
          selectedDimensions,
          successMetric,
          breakdownDimension,
          currentDate,
          startDate,
          endDate,
          matchType,
        }),
      )

      const hashUpdated = currentHash !== hash
      if (hashUpdated) {
        if (hash !== 0) {
          setFiltersHaveChanged(true)
        }
        setHash(currentHash)
      }
    }
  }, [
    currentReport,
    selectedDimensions,
    successMetric,
    breakdownDimension,
    currentDate,
    startDate,
    endDate,
    matchType,
  ])

  useEffect(() => {
    if (successMetric && campaignLinkDashboardTableRows === null) {
      getTableData()
    }
  }, [successMetric, currentDate, startDate, endDate])

  useEffect(() => {
    if (successMetric && campaignLinkDashboardTableRows !== null) {
      getTableData()
    }
  }, [matchType, selectedDimensions])

  const runReset = () => {
    if (currentReport !== null) {
      // eslint-disable-next-line
      // debugger
      const foundMatchType = getItemByKeyValue(
        matchTypeMerged,
        'value',
        currentReport.matchType,
      )
      if (foundMatchType) {
        setMatchType(foundMatchType)
      }
      setSuccessMetric(currentReport.selectedMetric)
      setBreakdownDimension(currentReport.stackDimension)
      setTitleValue(currentReport.reportTitle)
      const newSelectedDimensions = {}
      currentReport.applyFilters.forEach((item) => {
        newSelectedDimensions[item.dimensionName] = item.dimensionOptions
      })
      setSelectedDimensions(newSelectedDimensions)

      if (currentReport.endDate === '') {
        setCurrentDate(currentReport.startDate)

        // TODO: implement correct range based on currentDate
        // value : custom | thisMonth | lastMonth | thisQuarter | lastQuarter | thisYear
        setStartDate(moment(yesterday).subtract(365, 'days').toDate())
        setEndDate(yesterday)
      } else {
        setCurrentDate('custom')
        setStartDate(moment(currentReport.startDate).toDate())
        setEndDate(moment(currentReport.endDate).toDate())
        setCalendarVisible(false)
      }
    } else {
      setSelectedDimensions({})
      setMatchType(matchTypesDropDown[0])
      setGranularity('monthly')
      setCurrentDate('thisYear')
      setSuccessMetric(availableMetrics[0].metricID)
      setTitleValue(messages.defaultReportTitle)
    }
    setHash(0)
    setFiltersHaveChanged(false)
  }

  useEffect(() => {
    runReset()
  }, [currentReport])

  const {
    successMetricDisplayName,
    successMetricTooltip,
    isUplifterClickMetric,
    units,
    increasePositive,
  } = useMemo(() => {
    const result = getItemByKeyValue(
      allAvailableMetrics,
      'metricID',
      successMetric,
    )

    return {
      increasePositive: result !== -1 ? result.increasePositive : true,
      successMetricDisplayName: result !== -1 ? result.displayName : '',
      successMetricTooltip: result !== -1 ? result.description : '',
      isUplifterClickMetric:
        result !== -1 ? result.isUplifterClickMetric : false,
      units: result !== -1 ? result.units : 'int',
    }
  }, [successMetric])

  const dateRangeLabel = useMemo(() => {
    const currentDateItem = getItemByKeyValue(
      performanceDates,
      'metricValue',
      currentDate,
    )
    if (
      currentDateItem &&
      currentDateItem.label &&
      currentDateItem.metricValue
    ) {
      if (currentDateItem.metricValue === 'custom') {
        const from = moment(startDate).format(dateFormatShort)
        const to = moment(endDate).format(dateFormatShort)
        return ` from ${from} to ${to}`
      }

      return ` over the ${currentDateItem.label}`
    }
    return ''
  }, [currentDate, startDate, endDate])

  const dataBasedDateRange = useMemo(() => {
    if (graphDateRange && graphDateRange.length > 0) {
      const start = moment(graphDateRange[0], 'YYYY-MM-DD').format(
        dateFormatShort,
      )
      const end = moment(
        graphDateRange[graphDateRange.length - 1],
        'YYYY-MM-DD',
      ).format(dateFormatShort)
      return `${start} - ${end}`
    }
    return ''
  }, [graphDateRange])

  const graphDataIsLoading = useMemo(() => {
    return graphqlLoading.indexOf('campaignLinkDashboardGraph') !== -1
  }, [graphqlLoading])

  const tableDataIsLoading = useMemo(() => {
    return graphqlLoading.indexOf('campaignLinkDashboardTableRows') !== -1
  }, [graphqlLoading])

  const acquisitionActionsButtons = useCallback(() => {
    return (
      <div className={styles.actionButtonsWrapper}>
        <AcquisitionActions
          onEditMetric={() => {
            setShowModal(true)
          }}
          className={styles.actionButton}
          granularity={granularity}
          selectedMetric={successMetric}
          stackDimension={breakdownDimension}
          currentDate={currentDate}
          startDate={startDate}
          endDate={endDate}
          selectedDimensions={selectedDimensions}
        >
          <ManageReportsTabsSaveButton
            currentDate={currentDate}
            actionButton
            disabled={!filtersHaveChanged}
            currentReport={currentReport}
            selectedDimensions={selectedDimensions}
            startDate={startDate}
            endDate={endDate}
            matchType={matchType}
            breakdownDimension={breakdownDimension}
            successMetric={successMetric}
            onSaveReport={() => {
              setFiltersHaveChanged(false)
            }}
          />
        </AcquisitionActions>
      </div>
    )
  }, [
    successMetricsList,
    allAvailableMetrics,
    granularity,
    successMetric,
    breakdownDimension,
    currentDate,
    startDate,
    endDate,
    selectedDimensions,
    filtersHaveChanged,
    currentReport,
  ])

  if (
    selected &&
    (selected.preSetup ||
      (selected.blankAccount && selected.dataSource !== 'AA_REPORT') ||
      selected.billingState === 'preSetup')
  ) {
    const billingNotSet = selected.billingState === 'preSetup'
    return (
      <SiteWrapper>
        <Layout width={1600}>
          <Intro />
          <ConnectAnalyticsBlocker billingNotSet={billingNotSet} />
        </Layout>
      </SiteWrapper>
    )
  }
  const pageTitle = 'Performance report'

  if (errorLoadingInterface) {
    return (
      <SiteWrapper>
        <Layout width={1600}>
          <Intro title={pageTitle}>
            <p>We are having trouble loading your dashboard data...</p>
          </Intro>
        </Layout>
      </SiteWrapper>
    )
  }

  const onResetFilters = () => {
    setSelectedDimensions({})
  }

  const onResetReport = () => {
    runReset()
  }

  const onChangeFilters = (val: string[], item: any) => {
    const dimension = { ...selectedDimensions }
    delete dimension[item.dimensionName]
    dimension[item.dimensionName] = val
    setSelectedDimensions({ ...dimension })
  }

  const reverse = increasePositive ? !showTopLinksTable : showTopLinksTable
  return (
    <SiteWrapper grey>
      <Layout width={1600}>
        <div
          ref={(element) => {
            if (element) {
              const container = element.getBoundingClientRect()
              setWidth(container.width - 42)
            }
          }}
        >
          <Intro title={pageTitle} className={styles.intro}>
            <p>Create custom reports which compare marketing performance.</p>
          </Intro>
          <CollapsableFilters
            accountID={currentAccount?.accountID || ''}
            isMobile={isMobile}
            data={availableDimensions}
            selectedDimensions={selectedDimensions}
            onResetFilters={onResetFilters}
            onChange={(val: string[], item: any) => {
              onChangeFilters(val, item)
            }}
          >
            <div className={styles.headerIntro}>
              <div className={styles.introCopyWrapper}>
                <div className={styles.introCopyBox}>
                  <span className={styles.introCopy}>Compare </span>
                  <SelectBoxTypeahead
                    ignoreOrder
                    lowerCaseDropDownItems
                    key={`${matchType.value}${!!availableDimensions}`}
                    inline
                    className={styles.matchTypeSelector}
                    id="matchType"
                    placeholder=""
                    keyName="name"
                    keyValue="value"
                    value={matchType.value}
                    showDefault
                    onInputChange={(val: string) => {
                      setDefaultSet(true)
                      const res = getItemByKeyValue(
                        matchTypeMerged,
                        'value',
                        val,
                      )
                      if (res !== -1) {
                        if (
                          availableDimensions &&
                          availableDimensions.length > 0
                        ) {
                          const found = getItemByKeyValue(
                            availableDimensions,
                            'dimensionName',
                            val,
                          )
                          if (found !== -1 && found.dimensionName) {
                            setBreakdownDimension(found.dimensionName)
                          } else {
                            const useData = [
                              ...sortData(
                                availableDimensions,
                                'dimensionName',
                                true,
                                null,
                                true,
                              ),
                            ]
                            setBreakdownDimension(useData[0].dimensionName)
                          }
                        }
                        setMatchType(res)
                        const actionData = {
                          action: 'matchtype-updated',
                          longDescription: val,
                          websiteSection: 'report',
                        }
                        dispatch(logActionAsync(actionData))
                      }
                    }}
                    showTooltips
                    items={matchTypeMerged}
                  />
                </div>
                <div className={styles.introCopyBox}>
                  <span className={styles.introCopy}> over </span>
                  <SelectBoxTypeahead
                    lowerCaseDropDownItems
                    key={currentDate}
                    inline
                    className={styles.successMetricSelector}
                    id="dateRange"
                    placeholder="Date range"
                    keyName="displayName"
                    keyValue="metricValue"
                    value={currentDate}
                    showDefault
                    onInputChange={(val: string): void => {
                      setCurrentDate(val)
                      if (val !== 'thisYear') {
                        setGranularity('daily')
                      }
                      const actionData = {
                        action: 'date-range-updated',
                        longDescription: val,
                        websiteSection: 'report',
                      }
                      dispatch(logActionAsync(actionData))
                    }}
                    items={performanceDates}
                  />
                  {currentDate === 'custom' && (
                    <Button
                      className={styles.calendarButton}
                      type="calendar"
                      onClick={() => {
                        setCalendarVisible(!calendarVisible)
                      }}
                    />
                  )}
                  {currentDate === 'custom' && calendarVisible && (
                    <div className={styles.datePickerWrapper}>
                      <div className={styles.datePickerWrapperInner}>
                        <DatePicker
                          onChange={(e: any): void => {
                            const start = e.selection.startDate
                            const end = e.selection.endDate

                            setStartDate(start)
                            setEndDate(end)
                          }}
                          // @ts-ignore
                          maxDate={yesterday}
                          ranges={[
                            {
                              startDate,
                              endDate,
                              key: 'selection',
                            },
                          ]}
                        />
                        <Button
                          className={styles.closeButton}
                          type="container"
                          onClick={() => {
                            setCalendarVisible(!calendarVisible)
                            const st = moment(startDate).format('YYYY-MM-DD')
                            const en = moment(endDate).format('YYYY-MM-DD')
                            const days = moment(endDate).diff(startDate, 'days')
                            if (st !== en) {
                              const actionData = {
                                action: 'date-range-updated',
                                longDescription: `custom - start: ${st} end: ${en} days: ${days}`,
                                websiteSection: 'report',
                              }
                              dispatch(logActionAsync(actionData))
                            }
                          }}
                        >
                          Filter on date range
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.introCopyBox}>
                  <span className={styles.introCopy}> ranked by </span>
                  <SelectBoxTypeahead
                    lowerCaseDropDownItems
                    key={successMetric}
                    inline
                    className={styles.successMetricSelector}
                    id="successMetric"
                    placeholder="Success Metric"
                    keyName="displayName"
                    keyValue="metricID"
                    value={successMetric}
                    showDefault
                    onInputChange={(val: string) => {
                      setSuccessMetric(val)
                      const actionData = {
                        action: 'selected-metric-updated',
                        longDescription: val,
                        websiteSection: 'report',
                      }
                      dispatch(logActionAsync(actionData))
                    }}
                    items={allAvailableMetrics}
                  >
                    <span className={styles.itemLink}>
                      <p>
                        <Button
                          type="text"
                          onClick={() => {
                            setShowModal(true)
                          }}
                        >
                          Edit metrics
                        </Button>
                      </p>
                    </span>
                  </SelectBoxTypeahead>
                </div>
                <div className={styles.introCopyBox}>
                  <span className={styles.introCopy}> grouped by </span>
                  <SelectBoxTypeahead
                    lowerCaseDropDownItems
                    inline
                    key={granularity}
                    className={styles.granularitySelector}
                    id="granularity"
                    placeholder=""
                    keyName="name"
                    keyValue="value"
                    value={granularity}
                    showDefault
                    onInputChange={(val: string) => {
                      setGranularity(val)
                      const actionData = {
                        action: 'granularity-updated',
                        longDescription: val,
                        websiteSection: 'report',
                      }
                      dispatch(logActionAsync(actionData))
                    }}
                    items={granularityFilterData}
                  />
                </div>
              </div>
            </div>
          </CollapsableFilters>
          <ManageReportsTabs
            actionButtons={acquisitionActionsButtons()}
            onSaveReport={() => {
              setFiltersHaveChanged(false)
            }}
            showSaveButton={filtersHaveChanged}
            onResetReport={onResetReport}
            selectedDimensions={selectedDimensions}
            currentDate={currentDate}
            startDate={startDate}
            endDate={endDate}
            matchType={matchType}
            breakdownDimension={breakdownDimension}
            successMetric={successMetric}
            onChangeReport={(selectedReport) => {
              setCurrentReport(selectedReport)
            }}
          />
          <div className={styles.paper}>
            <div className={styles.paperHeader}>
              {isMobile && (
                <div className={styles.logoWrapper}>
                  {/* Deliberately excludes logo uploader - avoid showing 'replace' button */}
                  <Logo logoLink={logoLink} loading={!currentAccount} />
                </div>
              )}
              <div className={styles.paperHeaderContent}>
                <ButtonRow className={styles.headerButtonRow}>
                  <div className={styles.editTitleWrapper}>
                    <ClickEditInput
                      key={titleValue}
                      id="title"
                      name="title"
                      value={titleValue}
                      className={styles.editTitle}
                      onChange={(newValue: string) => {
                        setTitleValue(newValue)
                        if (currentReport !== null) {
                          const updateReportData = {
                            savedReportID: currentReport.savedReportID,
                            reportTitle: newValue,
                          }
                          dispatch(
                            updateSavedLinkPerformanceReportGQL(
                              updateReportData,
                            ),
                          )
                        }

                        const actionData = {
                          action: 'link-performance-title-updated',
                          longDescription: newValue,
                          websiteSection: 'report',
                        }
                        dispatch(logActionAsync(actionData))
                      }}
                    />
                  </div>
                </ButtonRow>
                <p className={styles.dateRangePar}>
                  <strong>Date range:</strong>{' '}
                  {dataBasedDateRange ? (
                    `${dataBasedDateRange}`
                  ) : (
                    <LoadingLabel label="Loading" />
                  )}
                </p>
                {dataSourceDescription && (
                  <ReactMarkdown
                    source={
                      dataSourceDescription
                        ? dataSourceDescription.replace(
                            'Metrics from: ',
                            '**Metrics from:** ',
                          )
                        : ''
                    }
                  />
                )}
                {!dataSourceDescription && (
                  <p className={styles.dateRangePar}>
                    <strong>Metrics from:</strong>{' '}
                    <LoadingLabel label="Loading" />
                  </p>
                )}
                {!(successMetricDisplayName && successMetricTooltip) && (
                  <p>
                    <strong>Key metric:</strong>{' '}
                    <LoadingLabel label="Loading" />
                  </p>
                )}
                {successMetricDisplayName && successMetricTooltip && (
                  <p>
                    <strong>{successMetricDisplayName}:</strong>{' '}
                    <span>{successMetricTooltip}</span>
                  </p>
                )}
                <CollapsableFiltersSummary
                  data={availableDimensions || []}
                  selectedDimensions={selectedDimensions}
                  onChange={(val: string[], item: any) => {
                    onChangeFilters(val, item)
                  }}
                />
              </div>

              {!isMobile && (
                <div className={styles.logoWrapper}>
                  {(isAdminUser || isSupportUser) && (
                    <ImgUploader
                      logoLink={logoLink}
                      imgType="logo"
                      uploadFn={async (file: File) => {
                        const res = await uploadAccountLogo({ file })
                        if (res === true) {
                          dispatch(getUserAccountDetailsGQL())
                          const actionData = {
                            action: 'company-logo-changed',
                            longDescription: '',
                            websiteSection: 'settings',
                          }
                          dispatch(logActionAsync(actionData))
                          return true
                        }
                        return false
                      }}
                      className={styles.reportLogo}
                    />
                  )}
                  {!isAdminUser && !isSupportUser && (
                    <Logo logoLink={logoLink} loading={!currentAccount} />
                  )}
                </div>
              )}
            </div>
            <MetricDataBanner
              loading={
                graphqlLoading.indexOf('campaignLinkDashboardMetricData') !== -1
              }
              granularity={granularity}
              dateRange={dataBasedDateRange}
              data={
                isUplifterClickMetric
                  ? dashboardMetricClickData
                  : dashboardMetricData
              }
              successMetric={successMetricDisplayName}
            />

            <div className={styles.graphWrapper}>
              <PerformanceReportGraph
                title={
                  breakdownDimension === ''
                    ? 'No breakdown'
                    : breakdownDimension
                }
                loading={graphDataIsLoading}
                units={units}
                graphWidth={graphWidth}
                offset={offset}
                granularity={granularity}
                isMobile={isMobile}
                graphDateRange={
                  isUplifterClickMetric
                    ? graphDateRangeClickData
                    : graphDateRange
                }
                graphData={isUplifterClickMetric ? graphClickData : graphData}
                graphType={
                  isUplifterClickMetric ? graphTypeClickData : graphType
                }
                availableDimensions={availableDimensions}
                breakdownDimension={breakdownDimension}
                width={width}
                successMetricDisplayName={successMetricDisplayName}
              >
                <EditablePageTitle
                  loading={graphDataIsLoading}
                  successMetricDisplayName={successMetricDisplayName}
                  setBreakdownDimension={setBreakdownDimension}
                  dateRangeLabel={dateRangeLabel}
                  breakdownDimension={breakdownDimension}
                  availableDimensions={availableDimensions}
                />
              </PerformanceReportGraph>
            </div>
            <div>
              <SupportMetricDropDown
                accountID={currentAccount?.accountID || ''}
                successMetricsList={successMetricsList}
                allAvailableMetrics={allAvailableMetrics}
                setSuccessMetricsList={setSuccessMetricsList}
              />
            </div>
            <div className={styles.tableWrapper}>
              <LinksTable
                matchType={matchType.value}
                reverse={reverse}
                currentMetricID={successMetric}
                showColumns={
                  successMetricsList.length === 0
                    ? [successMetric]
                    : successMetricsList
                }
                loading={!defaultSet || tableDataIsLoading}
                showHighlight={showHighlight}
                updateShowHighlight={updateShowHighlight}
              >
                {showTopLinksTable ? (
                  <>
                    <h2 className={styles.tableTitle}>
                      <Button
                        className={styles.headerButton}
                        type="none"
                        onClick={() => {
                          setShowTopLinksTable(false)
                        }}
                      >
                        <AnomalyDiff
                          arrowOnly
                          largeIcon
                          compact
                          actual={2}
                          expected={1}
                        />
                        <span className={styles.titleText}>
                          Top performing {matchType.shortName}
                        </span>
                      </Button>
                    </h2>
                    <ToolTip
                      showUnder
                      showOnLeft={isMobile}
                      className={styles.titleTooltip}
                      message={`The top 50% of campaign ${matchType.shortName}, ranked by ${successMetricDisplayName}, based on your chosen filters.`}
                    />
                  </>
                ) : (
                  <>
                    <h2 className={styles.tableTitle}>
                      <Button
                        className={styles.headerButton}
                        type="none"
                        onClick={() => {
                          setShowTopLinksTable(true)
                        }}
                      >
                        <AnomalyDiff
                          arrowOnly
                          largeIcon
                          compact
                          actual={1}
                          expected={2}
                        />
                        <span className={styles.titleText}>
                          Underperforming {matchType.shortName}
                        </span>
                      </Button>
                    </h2>
                    <ToolTip
                      showUnder
                      showOnLeft={isMobile}
                      className={styles.titleTooltip}
                      message={`The bottom 50% of campaign ${matchType.shortName}, ranked by ${successMetricDisplayName}, based on your chosen filters.`}
                    />
                  </>
                )}
              </LinksTable>
            </div>
          </div>
        </div>
        <Manage
          active={showModal}
          isReport
          toggleActive={() => {
            setShowModal(false)
          }}
        />
      </Layout>
    </SiteWrapper>
  )
}
